import { Grid2 as Grid, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { ListItem, useListContext } from 'framework/list';
import { useShowContext } from 'framework/show';
import { Alert, PlusIcon, Tooltip } from 'framework/ui/mui-builder';
import { DATE_FORMAT } from 'framework/utils';
import UpcomingChangesBadge from './UpcomingChangesBadge';
import { FormattedTextField } from 'framework/ui/field';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import useUpcomingChangesService from './upcomingChangesService';
import BackupModal from './createUpdate/BackupModal';
import { FormFieldsData } from 'framework/form';
import { useEmployeeService, EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { useAssignmentService } from 'domain/shared/assignment';
import { BackupInitialData } from './types';
import { DashboardItemSectionWrapper } from 'framework/ui/dashboard';
import { useDateService } from 'framework/utils';
import {
  CalendarPrefixIcon,
  ModalAccessButton,
  ProjectBackupTeamField,
  ProjectDatePeriodField,
  UPDATE_UPCOMING_CHANGE,
  getProjectManagerAlias,
} from 'domain/shared/newProject';
import { PRIVATE_MEMBERSHIP_ROLES } from '../core/constants';
import { UpcomingChangeStatus, UpcomingChangeType } from './constants';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';

interface BackupListProps {
  items: ListItem[];
  type: UpcomingChangeType;
  isTeamTab?: boolean;
}

const BackupList = ({ items, type, isTeamTab }: BackupListProps) => {
  const { data: projectData } = useShowContext();
  const { reloadData = () => {} } = useListContext();
  const { getCurrentDate } = useDateService();

  const { createUpcomingChange, updateUpcomingChange, deleteUpcomingChange } = useUpcomingChangesService();
  const { getById: getEmployeeById } = useEmployeeService();
  const { createAssignment, getAssignmentById, update, deleteById } = useAssignmentService();
  const { getUserAlias } = useAuthenticatedUserService();

  const [openModal, setOpenModal] = useState(false);
  const [modalFormData, setModalFormData] = useState<BackupInitialData | undefined>();

  const handleAddClick = () => {
    setModalFormData(undefined);
    setOpenModal(true);
  };

  const handleEditClick = async (item: ListItem) => {
    let assignmentData: Dictionary<any> = {};

    if (item.relatedAssignmentId) {
      const assignment = await getAssignmentById(item.relatedAssignmentId);
      assignmentData = {
        relatedAssignmentId: assignment.id,
        slotId: assignment.slotId,
        minPercent: assignment.minPercent,
        maxPercent: assignment.maxPercent,
        isBench: assignment.bench,
        membershipRole: assignment.membershipRole,
        roleOnProject: assignment.roleOnProject,
      };
    } else {
      const existingTeamMember = projectData?.team?.members?.find(
        (member: any) => member.employeeAlias === item.fromEmployeeAlias,
      );

      assignmentData = {
        slotId: existingTeamMember?.slotId,
        isPrivate: PRIVATE_MEMBERSHIP_ROLES.includes(existingTeamMember?.role),
      };
    }

    setModalFormData({
      upcomingChangeId: item.upcomingChangeId,
      fromEmployeeAlias: item.fromEmployeeAlias,
      toEmployeeAlias: item.toEmployeeAlias,
      plannedStart: item.from,
      plannedEnd: item.to,
      comment: item.comment,
      isPrivate: item.isPrivate,
      ...assignmentData,
    });
    setOpenModal(true);
  };

  const handleSubmit = async (values: FormFieldsData) => {
    let relatedAssignmentId = values.relatedAssignmentId;
    if (values.toEmployeeAlias) {
      const employeeData = await getEmployeeById(values.toEmployeeAlias);
      const projectManager = getProjectManagerAlias(projectData?.team?.members);
      const ownerId = projectManager || (await getUserAlias()) || undefined;
      const assignmentCommand = {
        target: {
          type: 'PROJECT',
          id: projectData.legacyId,
        },
        serviceId: null,
        budgetId: projectData.budget?.id ? Number(projectData.budget?.id) : null,

        ownerId,
        backupForEmployeeId: type === UpcomingChangeType.BACKUP ? values.fromEmployeeAlias : null,
        employeeId: values.toEmployeeAlias,

        positionId: employeeData.positionId,
        technologyId: employeeData.specializationInfo?.technologyId,
        competencyLevel: employeeData.specializationInfo?.competencyLevel,

        backup: type === UpcomingChangeType.BACKUP,
        bench: values.isBench,
        from: values.plannedStart,
        to: values.plannedEnd,
        minPercent: values.minPercent,
        maxPercent: values.maxPercent,

        noteText: values.comment,

        slotId: values.slotId,
        membershipRole: values.membershipRole,

        roleOnProject: null,
      };

      if (relatedAssignmentId) {
        await update(relatedAssignmentId, assignmentCommand);
      } else {
        const createdAssignment = await createAssignment(assignmentCommand);
        relatedAssignmentId = createdAssignment.id;
      }
    }

    const upcomingChangeCommand = {
      type: type,
      status: values.toEmployeeAlias ? UpcomingChangeStatus.ASSIGNED : UpcomingChangeStatus.PENDING,
      plannedStart: values.plannedStart,
      plannedEnd: values.plannedEnd,
      items: [
        {
          fromEmployeeAlias: values.fromEmployeeAlias,
          toEmployeeAlias: values.toEmployeeAlias,
          comment: values.comment,
          relatedAssignmentId: relatedAssignmentId,
        },
      ],
      isPrivate: values.isPrivate || false,
    };

    if (values.upcomingChangeId) {
      return updateUpcomingChange(values.upcomingChangeId, upcomingChangeCommand);
    } else {
      return createUpcomingChange(projectData.id, upcomingChangeCommand);
    }
  };

  const onDelete = async (values: FormFieldsData) => {
    await deleteUpcomingChange(values.upcomingChangeId);
    if (values.relatedAssignmentId) {
      await deleteById(values.relatedAssignmentId);
    }
    return reloadData?.();
  };

  const renderActions = useCallback(() => {
    return (
      <IconButton sx={!isTeamTab ? { p: 0 } : { p: 'auto' }} key="add" onClick={handleAddClick}>
        <PlusIcon />
      </IconButton>
    );
  }, [isTeamTab]);

  const today = getCurrentDate(DATE_FORMAT.API_ONLY_DATE).format(DATE_FORMAT.API_ONLY_DATE);
  const visibleItems = items.filter(item => !item.to || item.to >= today);

  return (
    <>
      <DashboardItemSectionWrapper
        sx={{
          mb: !isTeamTab ? '0!important' : 'auto',
          '& .MuiAccordionSummary-root': {
            pr: !isTeamTab ? '0!important' : 'auto',
          },

          '& .MuiAccordionDetails-root': { p: '0!important' },
        }}
        title={
          <Grid container direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Grid size={2.4}>
              <Typography variant="body1">{type === UpcomingChangeType.BACKUP ? 'Backup' : 'Replacement'}</Typography>
            </Grid>

            <Grid size={2}>
              <Typography variant="body1" color="textSecondary">
                {visibleItems.length} scheduled
              </Typography>
            </Grid>

            <Grid size="grow" />

            <Grid size="auto">{renderActions()}</Grid>
          </Grid>
        }
        defaultExpanded
      >
        {visibleItems.length === 0 && (
          <Alert severity="info">No {type === UpcomingChangeType.BACKUP ? 'backups' : 'replacements'} scheduled</Alert>
        )}
        {visibleItems.length > 0 && (
          <>
            {visibleItems.map(item => (
              <Grid
                container
                size={12}
                key={item.id}
                direction="row"
                spacing={1}
                sx={{ p: isTeamTab ? '8px 16px' : '8px 0 8px 16px' }}
                alignItems="center"
              >
                <Grid size={2.5}>
                  <EmployeeLinkFieldWithNameLoading
                    ignoredNameParts={['middleName', 'maidenName']}
                    source={'fromEmployeeAlias'}
                    value={item.fromEmployeeAlias}
                  />
                </Grid>
                <Grid size={2.5}>
                  <ProjectDatePeriodField
                    isShowEmptyTo={item.to}
                    fromProps={{
                      label: '',
                      source: 'from',
                      value: item.from,
                      tooltipProps: {
                        title: `${type === UpcomingChangeType.BACKUP ? 'Backup' : 'Replacement'} period`,
                        children: <CalendarPrefixIcon />,
                      },
                      dateFormat: DATE_FORMAT.FULL_DATE,
                    }}
                    toProps={{
                      label: '',
                      source: 'to',
                      value: item.to,
                      dateFormat: DATE_FORMAT.FULL_DATE,
                    }}
                  />
                </Grid>
                <Grid size={1}>
                  <UpcomingChangesBadge source={'status'} value={item.status} />
                </Grid>
                <Grid size={2}>
                  <ProjectBackupTeamField
                    source={'toEmployeeAlias'}
                    value={item.toEmployeeAlias}
                    tooltipTitle={type === UpcomingChangeType.BACKUP ? 'Backup' : 'Replacement'}
                  />
                </Grid>
                <Grid size={3.5}>
                  <FormattedTextField
                    format={(str: string) =>
                      str && str.length > 40 ? (
                        <Tooltip title={str}>
                          <div>
                            <Typography
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2,
                                wordBreak: 'break-word',
                              }}
                            >
                              {str}
                            </Typography>
                          </div>
                        </Tooltip>
                      ) : (
                        str
                      )
                    }
                    source={'comment'}
                    value={item.comment}
                    label={''}
                  />
                </Grid>
                <Grid
                  size={0.5}
                  sx={{
                    '.MuiFormControl-root': { mb: !isTeamTab ? 0 : 'auto' },
                    display: 'flex',
                    justifyContent: 'end',
                  }}
                >
                  <ModalAccessButton
                    permission={UPDATE_UPCOMING_CHANGE}
                    IconComponent={AppRegistrationIcon}
                    onClick={() => handleEditClick(item)}
                    isTeamTab={isTeamTab}
                  />
                </Grid>
              </Grid>
            ))}
          </>
        )}
      </DashboardItemSectionWrapper>
      <BackupModal
        modalOpen={openModal}
        setModalOpen={setOpenModal}
        type={type}
        teamStructure={projectData.team}
        initialData={modalFormData}
        onDelete={onDelete}
        handleSubmit={handleSubmit}
        afterSubmit={{
          execute: response => {
            reloadData?.();
          },
        }}
      />
    </>
  );
};

export default BackupList;
